import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { AuthPage } from '@/features/auth/AuthPage';
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage';
import { AuthResetPage } from '@/features/auth/AuthResetPage';
import { AuthInvitePage } from '@/features/auth/AuthInvitePage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { OrderTrackerPage } from '@/features/orders/OrderTrackerPage';
import { OrderDetailPage } from '@/features/orders/OrderDetailPage';
import { AdminOrdersBinPage } from '@/features/admin/orders/AdminOrdersBinPage';
// PatientsListPage / PatientDetailPage imports removed when the /patients
// nav entry + routes were hidden. To re-enable: re-add these imports, the
// route block below the /orders/:id route, and the nav entry in AppShell.
import { ClinicPage } from '@/features/clinic/ClinicPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { AgentDashboardPage } from '@/features/agent/AgentDashboardPage';
import { ProviderDashboardPage } from '@/features/provider/ProviderDashboardPage';
import { ProviderPipelinePage } from '@/features/provider/ProviderPipelinePage';
import { ProviderLineDetailPage } from '@/features/provider/ProviderLineDetailPage';
import { PatientPublicPage } from '@/features/patient-public/PatientPublicPage';
import {
  PatientReorderPage,
  PatientReorderSuccessPage,
} from '@/features/patient-public/PatientReorderPage';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { RouteErrorBoundary } from '@/components/layout/RouteErrorBoundary';

/**
 * Phase 7 Track 1 — Fix 6: route-level code splitting.
 *
 * The wizard (`/orders/new`) is the heaviest component in the app — it pulls
 * in the prescription form + AnatomicalFootDiagram + product picker. Lazy
 * loading it shaves ~120KB off the initial bundle.
 *
 * Admin pages are also lazy-loaded as one chunk per route family — most
 * podiatrist users never visit `/admin/*`, so there's no reason to ship
 * those bundles up-front. Same for the playground (designer concepts that
 * are only opened by Rafael during reviews).
 */
const NewOrderPage = lazy(() =>
  import('@/features/orders/wizard/NewOrderPage').then((m) => ({ default: m.NewOrderPage })),
);
const AdminPage = lazy(() =>
  import('@/features/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const AdminClinicsListPage = lazy(() =>
  import('@/features/admin/clinics/AdminClinicsListPage').then((m) => ({
    default: m.AdminClinicsListPage,
  })),
);
const AdminClinicDetailPage = lazy(() =>
  import('@/features/admin/clinics/AdminClinicDetailPage').then((m) => ({
    default: m.AdminClinicDetailPage,
  })),
);
const AdminProductsHubPage = lazy(() =>
  import('@/features/admin/products/AdminProductsHubPage').then((m) => ({
    default: m.AdminProductsHubPage,
  })),
);
const AdminProductDetailPage = lazy(() =>
  import('@/features/admin/products/AdminProductDetailPage').then((m) => ({
    default: m.AdminProductDetailPage,
  })),
);
const AdminProvidersPage = lazy(() =>
  import('@/features/admin/providers/AdminProvidersPage').then((m) => ({
    default: m.AdminProvidersPage,
  })),
);
const AdminEmailTemplatesPage = lazy(() =>
  import('@/features/admin/email-templates/AdminEmailTemplatesPage').then((m) => ({
    default: m.AdminEmailTemplatesPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/features/admin/users/AdminUsersPage').then((m) => ({
    default: m.AdminUsersPage,
  })),
);
const AdminLeadsPage = lazy(() =>
  import('@/features/admin/leads/AdminLeadsPage').then((m) => ({ default: m.AdminLeadsPage })),
);
const LeadFinderPage = lazy(() =>
  import('@/features/admin/leads/LeadFinderPage').then((m) => ({ default: m.LeadFinderPage })),
);
const AdminHomeServicesPage = lazy(() =>
  import('@/features/admin/home-services/AdminHomeServicesPage').then((m) => ({
    default: m.AdminHomeServicesPage,
  })),
);
const AdminHomeServiceDetailPage = lazy(() =>
  import('@/features/admin/home-services/AdminHomeServiceDetailPage').then((m) => ({
    default: m.AdminHomeServiceDetailPage,
  })),
);
const CorporateDetailPage = lazy(() =>
  import('@/features/admin/home-services/CorporateDetailPage').then((m) => ({
    default: m.CorporateDetailPage,
  })),
);
const AgentDetailPage = lazy(() =>
  import('@/features/admin/home-services/AgentDetailPage').then((m) => ({
    default: m.AgentDetailPage,
  })),
);
const AdminReorderPricingPage = lazy(() =>
  import('@/features/admin/reorder-pricing/AdminReorderPricingPage').then((m) => ({
    default: m.AdminReorderPricingPage,
  })),
);
const AdminCommissionsPage = lazy(() =>
  import('@/features/admin/commissions/AdminCommissionsPage').then((m) => ({
    default: m.AdminCommissionsPage,
  })),
);
// R35 — Zoho supplier-invoice processing module (admin-only).
const AdminZohoLayout = lazy(() =>
  import('@/features/admin/zoho/AdminZohoLayout').then((m) => ({ default: m.AdminZohoLayout })),
);
const AdminZohoInvoicesPage = lazy(() =>
  import('@/features/admin/zoho/AdminZohoInvoicesPage').then((m) => ({
    default: m.AdminZohoInvoicesPage,
  })),
);
const AdminZohoInvoiceDetailPage = lazy(() =>
  import('@/features/admin/zoho/AdminZohoInvoiceDetailPage').then((m) => ({
    default: m.AdminZohoInvoiceDetailPage,
  })),
);
const AdminZohoVendorsPage = lazy(() =>
  import('@/features/admin/zoho/AdminZohoVendorsPage').then((m) => ({
    default: m.AdminZohoVendorsPage,
  })),
);
const AdminZohoConnectPage = lazy(() =>
  import('@/features/admin/zoho/AdminZohoConnectPage').then((m) => ({
    default: m.AdminZohoConnectPage,
  })),
);
const PlaygroundIndex = lazy(() =>
  import('@/playground/prescription-assistant/PlaygroundIndex').then((m) => ({
    default: m.PlaygroundIndex,
  })),
);
const ConceptA = lazy(() =>
  import('@/playground/prescription-assistant/concept-a/ConceptA').then((m) => ({
    default: m.ConceptA,
  })),
);
const ConceptB = lazy(() =>
  import('@/playground/prescription-assistant/concept-b/ConceptB').then((m) => ({
    default: m.ConceptB,
  })),
);
const ConceptC = lazy(() =>
  import('@/playground/prescription-assistant/concept-c/ConceptC').then((m) => ({
    default: m.ConceptC,
  })),
);

/** Lightweight route fallback — avoids a flash-of-blank for the lazy chunks. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, isProvider } = useAuth();
  if (!session) return <Navigate to="/auth" replace />;
  if (isProvider) return <Navigate to="/provider" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { session, isAdmin, isProvider } = useAuth();
  if (!session) return <Navigate to="/auth" replace />;
  // Provider-role users are redirected to their own home rather than being
  // sent to /dashboard (which they can't read either). This keeps role
  // isolation strict — Phase 4d brief §5.
  if (!isAdmin) return <Navigate to={isProvider ? '/provider' : '/dashboard'} replace />;
  return <>{children}</>;
}

/** HomeServicesRoute — admits admins AND the home_services_manager role
 *  (read/track access to the Home Services admin section only). Every other
 *  /admin/* route keeps plain AdminRoute, so a home_services_manager who is not
 *  an admin is blocked everywhere else (and RLS returns zero rows there too).
 *  Waits for the role set to resolve on a cold/hard navigation so the manager
 *  isn't bounced before their roles load. */
function HomeServicesRoute({ children }: { children: React.ReactNode }) {
  const { session, isAdmin, isHomeServicesManager, isProvider, profileLoading } = useAuth();
  if (!session) return <Navigate to="/auth" replace />;
  if (profileLoading) return <RouteFallback />;
  if (!isAdmin && !isHomeServicesManager) {
    return <Navigate to={isProvider ? '/provider' : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

function ProviderRoute({ children }: { children: React.ReactNode }) {
  const { session, isProvider, isAdmin } = useAuth();
  if (!session) return <Navigate to="/auth" replace />;
  if (!isProvider && !isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Open to any signed-in user regardless of role. Used for /settings. */
function AnyRoleRoute({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  if (!session) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

/** RoleHome, `/` redirects each role to their canonical home.
 *
 * Priority: admin > provider > podiatrist > agent. Rationale:
 *   - admin always lands on the admin shell (even if they hold a second role)
 *   - provider's surface is narrow, so providers stay in their portal
 *   - a podiatrist + agent combo lands on /dashboard (their primary
 *     workspace) and visits /agent as a secondary nav pill
 *   - agent-only users land on /agent
 */
function RoleHome() {
  const { isAdmin, isProvider, isPodiatrist, isAgent, isHomeServicesManager } = useAuth();
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (isProvider) return <Navigate to="/provider" replace />;
  if (isPodiatrist) return <Navigate to="/dashboard" replace />;
  if (isAgent) return <Navigate to="/agent" replace />;
  // A home_services_manager with no other surface lands on their read-only
  // Home Services section (a bare podiatrist dashboard would be empty for them).
  if (isHomeServicesManager) return <Navigate to="/admin/home-services" replace />;
  return <Navigate to="/dashboard" replace />;
}

/** AgentRoute — admit agents (and admins, who can see everything). */
function AgentRoute({ children }: { children: React.ReactNode }) {
  const { session, isAgent, isAdmin } = useAuth();
  if (!session) return <Navigate to="/auth" replace />;
  if (!isAgent && !isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    // RouteErrorBoundary wraps Suspense so a failed `React.lazy()` chunk
    // load (common after a Vercel rebuild invalidates old chunk hashes
    // while a tab is still open) is caught and the user sees a "Reload"
    // button instead of being stuck on RouteFallback's spinner forever.
    // This is the most-likely root cause of the "pages loading forever"
    // reports — a stale tab visiting /admin/* hits a 404 on the chunk,
    // React throws inside Suspense, and without a boundary the spinner
    // never resolves.
    <RouteErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Patient-public — no auth, no nav (Phase 4e) */}
      <Route path="/p/:token" element={<PatientPublicPage />} />
      <Route path="/q/:shortCode" element={<PatientPublicPage />} />
      <Route path="/p/:token/reorder" element={<PatientReorderPage />} />
      <Route path="/q/:shortCode/reorder" element={<PatientReorderPage />} />
      <Route path="/p/:token/reorder/success" element={<PatientReorderSuccessPage />} />
      <Route path="/q/:shortCode/reorder/success" element={<PatientReorderSuccessPage />} />

      {/* Auth */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/reset" element={<AuthResetPage />} />
      <Route path="/auth/invite/:token" element={<AuthInvitePage />} />

      {/* Phase 3 playground — public for design preview */}
      <Route path="/playground/prescription-assistant" element={<PlaygroundIndex />} />
      <Route path="/playground/prescription-assistant/concept-a" element={<ConceptA />} />
      <Route path="/playground/prescription-assistant/concept-b" element={<ConceptB />} />
      <Route path="/playground/prescription-assistant/concept-c" element={<ConceptC />} />

      {/* Role-aware home */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RoleHome />
          </ProtectedRoute>
        }
      />

      {/* Podiatrist routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrderTrackerPage scope="podiatrist" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/new"
        element={
          <ProtectedRoute>
            <NewOrderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <OrderDetailPage scope="podiatrist" />
          </ProtectedRoute>
        }
      />
      {/* Patients routes removed per user request (033f835 follow-up): the
          surface adds complexity podiatrists don't need today. Imports +
          page components are kept above so re-enabling is a single block
          paste back into this Routes block + an entry in the AppShell nav. */}
      <Route
        path="/clinic"
        element={
          <ProtectedRoute>
            <ClinicPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <AnyRoleRoute>
            <SettingsPage />
          </AnyRoleRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <AdminRoute>
            <OrderTrackerPage scope="admin" />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/orders/:id"
        element={
          <AdminRoute>
            <OrderDetailPage scope="admin" />
          </AdminRoute>
        }
      />
      {/* Orders recycle bin (20270357). AdminRoute is navigation only; the
          real gate is the orders SELECT policy, which hides deleted rows
          from every role except admin. */}
      <Route
        path="/admin/orders/bin"
        element={
          <AdminRoute>
            <AdminOrdersBinPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/clinics"
        element={
          <AdminRoute>
            <AdminClinicsListPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/clinics/:id"
        element={
          <AdminRoute>
            <AdminClinicDetailPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <AdminRoute>
            <AdminProductsHubPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/products/:id"
        element={
          <AdminRoute>
            <AdminProductDetailPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/providers"
        element={
          <AdminRoute>
            <AdminProvidersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/email-templates"
        element={
          <AdminRoute>
            <AdminEmailTemplatesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        }
      />
      {/* Leads is one nav entry with two tabs (see LeadsTabStrip):
          /admin/leads = Inbound (enquiries), /admin/leads/prospecting = Outbound
          (lead finder). The old /admin/lead-finder path redirects to the
          Outbound tab so existing links keep working. */}
      <Route
        path="/admin/leads"
        element={
          <AdminRoute>
            <AdminLeadsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/leads/prospecting"
        element={
          <AdminRoute>
            <LeadFinderPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/lead-finder"
        element={<Navigate to="/admin/leads/prospecting" replace />}
      />
      <Route
        path="/admin/home-services"
        element={
          <HomeServicesRoute>
            <AdminHomeServicesPage />
          </HomeServicesRoute>
        }
      />
      <Route
        path="/admin/home-services/corporate/:id"
        element={
          <HomeServicesRoute>
            <CorporateDetailPage />
          </HomeServicesRoute>
        }
      />
      <Route
        path="/admin/home-services/agents/:id"
        element={
          <HomeServicesRoute>
            <AgentDetailPage />
          </HomeServicesRoute>
        }
      />
      <Route
        path="/admin/home-services/:id"
        element={
          <HomeServicesRoute>
            <AdminHomeServiceDetailPage />
          </HomeServicesRoute>
        }
      />
      <Route
        path="/admin/commissions"
        element={
          <AdminRoute>
            <AdminCommissionsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/reorder-pricing"
        element={
          <AdminRoute>
            <AdminReorderPricingPage />
          </AdminRoute>
        }
      />
      {/* R35 — Zoho supplier-invoice processing (admin-only).
          AdminZohoLayout enforces role internally, but we keep the
          outer AdminRoute wrapper for symmetry + double-defence with
          the rest of the /admin/* tree. The layout renders the
          sub-nav (Invoices / Vendors / Settings) + <Outlet />. */}
      <Route
        path="/admin/zoho"
        element={
          <AdminRoute>
            <AdminZohoLayout />
          </AdminRoute>
        }
      >
        <Route index element={<Navigate to="/admin/zoho/invoices" replace />} />
        <Route path="invoices" element={<AdminZohoInvoicesPage />} />
        <Route path="invoices/:id" element={<AdminZohoInvoiceDetailPage />} />
        <Route path="vendors" element={<AdminZohoVendorsPage />} />
        <Route path="connect" element={<AdminZohoConnectPage />} />
      </Route>

      {/* Agent routes Phase 7 R20 + Phase 2 R21.
          /agent is the consolidated tabbed surface (Orders + Retribution).
          /agent/orders is kept as a permanent redirect so any external
          bookmarks (or in-app deep links from earlier rounds) still land
          on the Orders tab. */}
      <Route
        path="/agent"
        element={
          <AgentRoute>
            <AgentDashboardPage />
          </AgentRoute>
        }
      />
      <Route
        path="/agent/orders"
        element={<Navigate to="/agent?tab=orders" replace />}
      />

      {/* Provider routes — Phase 4d */}
      <Route
        path="/provider"
        element={
          <ProviderRoute>
            <ProviderDashboardPage />
          </ProviderRoute>
        }
      />
      <Route
        path="/provider/orders"
        element={
          <ProviderRoute>
            <ProviderPipelinePage />
          </ProviderRoute>
        }
      />
      <Route
        path="/provider/orders/:lineId"
        element={
          <ProviderRoute>
            <ProviderLineDetailPage />
          </ProviderRoute>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
}
